import React, { useState, useRef, useEffect } from 'react';
import { WhiteboardObject } from '../../services/whiteboardService';
import './Whiteboard.css';

interface StickyNoteProps {
  note: WhiteboardObject;
  onUpdate: (noteId: string, updates: Partial<WhiteboardObject>) => void;
  onDelete: (noteId: string) => void;
  scale: number; // Canvas zoom scale
  panX: number; // Canvas pan X offset
  panY: number; // Canvas pan Y offset
}

const STICKY_NOTE_COLORS = [
  { name: 'yellow', value: '#ffeb3b' },
  { name: 'pink', value: '#f48fb1' },
  { name: 'blue', value: '#90caf9' },
  { name: 'green', value: '#a5d6a7' },
  { name: 'orange', value: '#ffcc80' },
  { name: 'purple', value: '#ce93d8' },
];

const StickyNote: React.FC<StickyNoteProps> = ({
  note,
  onUpdate,
  onDelete,
  scale,
  panX,
  panY
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [content, setContent] = useState(note.content || '');
  const [isTouching, setIsTouching] = useState(false);
  const [localPosition, setLocalPosition] = useState({ x: note.x, y: note.y });
  const noteRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  
  // Double-tap detection for mobile
  const lastTapRef = useRef<{ time: number; x: number; y: number } | null>(null);
  const touchStartPosRef = useRef<{ x: number; y: number } | null>(null);
  const hasMovedRef = useRef(false);
  const touchStartHandlerRef = useRef<((e: TouchEvent) => void) | null>(null);
  const dragEndPositionRef = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    setContent(note.content || '');
  }, [note.content]);

  // Update local position when note position changes from external source
  useEffect(() => {
    if (!isDragging) {
      setLocalPosition({ x: note.x, y: note.y });
    }
  }, [note.x, note.y, isDragging]);

  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.select();
    }
  }, [isEditing]);

  // Set up non-passive touch listener to allow preventDefault
  useEffect(() => {
    const noteElement = noteRef.current;
    if (!noteElement) return;

    const handleTouchStartNative = (e: TouchEvent) => {
      if (e.target === textareaRef.current || (e.target as HTMLElement).closest('.whiteboard-sticky-note-color-picker')) {
        return;
      }
      if (e.touches.length === 1) {
        const touch = e.touches[0];
        touchStartPosRef.current = { x: touch.clientX, y: touch.clientY };
        hasMovedRef.current = false;
        setIsTouching(true); // Track that we're touching the note
        
        const canvas = noteElement.closest('.whiteboard-canvas');
        if (canvas && noteElement) {
          const canvasRect = canvas.getBoundingClientRect();
          const canvasTouchX = (touch.clientX - canvasRect.left - panX) / scale;
          const canvasTouchY = (touch.clientY - canvasRect.top - panY) / scale;
          
          setDragStart({
            x: canvasTouchX - note.x,
            y: canvasTouchY - note.y
          });
        }
      }
    };

    touchStartHandlerRef.current = handleTouchStartNative;
    noteElement.addEventListener('touchstart', handleTouchStartNative, { passive: false });

    return () => {
      noteElement.removeEventListener('touchstart', handleTouchStartNative);
    };
  }, [panX, panY, scale, note.x, note.y]);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.target === textareaRef.current || (e.target as HTMLElement).closest('.whiteboard-sticky-note-color-picker')) {
      return; // Don't start drag if clicking on textarea or color picker
    }
    e.preventDefault();
    setIsDragging(true);
    
    // Get canvas container to calculate offset in canvas coordinates
    const canvas = noteRef.current?.closest('.whiteboard-canvas');
    if (canvas && noteRef.current) {
      const canvasRect = canvas.getBoundingClientRect();
      // Calculate offset in canvas coordinates (where the mouse is relative to note's canvas position)
      const canvasMouseX = (e.clientX - canvasRect.left - panX) / scale;
      const canvasMouseY = (e.clientY - canvasRect.top - panY) / scale;
      
      setDragStart({
        x: canvasMouseX - note.x,
        y: canvasMouseY - note.y
      });
    }
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging || !noteRef.current) return;

    // Get canvas container to calculate relative position
    const canvas = noteRef.current.closest('.whiteboard-canvas');
    if (!canvas) return;

    const canvasRect = canvas.getBoundingClientRect();
    
    // Calculate position relative to canvas, accounting for pan and scale
    const canvasX = (e.clientX - canvasRect.left - panX) / scale;
    const canvasY = (e.clientY - canvasRect.top - panY) / scale;
    
    // Subtract the drag start offset
    const newX = canvasX - dragStart.x;
    const newY = canvasY - dragStart.y;

    // Update local position only (no API call during drag)
    setLocalPosition({ x: newX, y: newY });
    dragEndPositionRef.current = { x: newX, y: newY };
  };

  const handleTouchMove = (e: TouchEvent) => {
    if (e.touches.length !== 1 || !touchStartPosRef.current || !noteRef.current) return;
    
    const touch = e.touches[0];
    
    // Check if touch has moved significantly (more than 5px)
    if (!hasMovedRef.current) {
      const deltaX = Math.abs(touch.clientX - touchStartPosRef.current.x);
      const deltaY = Math.abs(touch.clientY - touchStartPosRef.current.y);
      
      if (deltaX > 5 || deltaY > 5) {
        hasMovedRef.current = true;
        setIsDragging(true);
      } else {
        return; // Not enough movement yet
      }
    }
    
    // Now we're dragging - prevent default and move the note
    e.preventDefault(); // Prevent page scroll
    e.stopPropagation(); // Prevent canvas panning

    // Get canvas container to calculate relative position
    const canvas = noteRef.current.closest('.whiteboard-canvas');
    if (!canvas) return;

    const canvasRect = canvas.getBoundingClientRect();
    
    // Calculate position relative to canvas, accounting for pan and scale
    const canvasX = (touch.clientX - canvasRect.left - panX) / scale;
    const canvasY = (touch.clientY - canvasRect.top - panY) / scale;
    
    // Subtract the drag start offset
    const newX = canvasX - dragStart.x;
    const newY = canvasY - dragStart.y;

    // Update local position only (no API call during drag)
    setLocalPosition({ x: newX, y: newY });
    dragEndPositionRef.current = { x: newX, y: newY };
  };

  const handleMouseUp = () => {
    if (isDragging && dragEndPositionRef.current) {
      // Save position to backend when dragging stops
      onUpdate(note.id, { 
        x: dragEndPositionRef.current.x, 
        y: dragEndPositionRef.current.y 
      });
      dragEndPositionRef.current = null;
    }
    setIsDragging(false);
  };

  const handleTouchEndDocument = (e: TouchEvent) => {
    // This is called from document-level listener during dragging
    if (isDragging && dragEndPositionRef.current) {
      // Save position to backend when dragging stops
      onUpdate(note.id, { 
        x: dragEndPositionRef.current.x, 
        y: dragEndPositionRef.current.y 
      });
      dragEndPositionRef.current = null;
    }
    setIsDragging(false);
    setIsTouching(false);
    touchStartPosRef.current = null;
    hasMovedRef.current = false;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    // Skip double-tap detection if we were dragging (document handler will clean up)
    if (isDragging || hasMovedRef.current) {
      return;
    }

    // Only check for double-tap if there was no movement (it was a tap, not a drag)
    if (touchStartPosRef.current) {
      const now = Date.now();
      const currentTap = {
        time: now,
        x: touchStartPosRef.current.x,
        y: touchStartPosRef.current.y
      };
      
      // Check if this is a double-tap
      if (lastTapRef.current) {
        const timeDiff = now - lastTapRef.current.time;
        const distance = Math.sqrt(
          Math.pow(currentTap.x - lastTapRef.current.x, 2) +
          Math.pow(currentTap.y - lastTapRef.current.y, 2)
        );
        
        // Double-tap detected: within 300ms and within 50px distance
        if (timeDiff < 300 && distance < 50) {
          e.preventDefault();
          setIsEditing(true);
          lastTapRef.current = null; // Reset to prevent triple-tap
          touchStartPosRef.current = null;
          hasMovedRef.current = false;
          return;
        }
      }
      
      // Store this tap for potential double-tap detection
      lastTapRef.current = currentTap;
    }
    
    touchStartPosRef.current = null;
    hasMovedRef.current = false;
  };

  useEffect(() => {
    // Attach listeners when touching or dragging
    if (isTouching || isDragging) {
      if (isDragging) {
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
      }
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleTouchEndDocument);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEndDocument);
      };
    }
  }, [isTouching, isDragging, dragStart, scale, panX, panY, note.x, note.y]);

  const handleContentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setContent(e.target.value);
  };

  const handleContentBlur = () => {
    setIsEditing(false);
    if (content !== note.content) {
      onUpdate(note.id, { content });
    }
  };

  const handleDoubleClick = () => {
    setIsEditing(true);
  };

  const handleColorSelect = (color: string) => {
    onUpdate(note.id, { color });
    setShowColorPicker(false);
  };

  const handleDelete = () => {
    if (window.confirm('Delete this sticky note?')) {
      onDelete(note.id);
    }
  };

  const currentColor = note.color || STICKY_NOTE_COLORS[0].value;

  return (
    <div
      ref={noteRef}
      className="whiteboard-sticky-note"
      style={{
        left: `${localPosition.x}px`,
        top: `${localPosition.y}px`,
        width: `${note.width}px`,
        height: `${note.height}px`,
        backgroundColor: currentColor,
        cursor: isDragging ? 'grabbing' : 'grab',
        touchAction: 'none'
      }}
      onMouseDown={handleMouseDown}
      onTouchEnd={handleTouchEnd}
      onDoubleClick={handleDoubleClick}
    >
      <div className="whiteboard-sticky-note-header">
        <div className="whiteboard-sticky-note-color-wrapper">
          <button
            className="whiteboard-sticky-note-color-btn"
            onClick={(e) => {
              e.stopPropagation();
              setShowColorPicker(!showColorPicker);
            }}
            style={{ backgroundColor: currentColor }}
            title="Change color"
          />
          {showColorPicker && (
            <div className="whiteboard-sticky-note-color-picker">
              {STICKY_NOTE_COLORS.map((color) => (
                <button
                  key={color.name}
                  className="whiteboard-sticky-note-color-option"
                  style={{ backgroundColor: color.value }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleColorSelect(color.value);
                  }}
                  title={color.name}
                />
              ))}
            </div>
          )}
        </div>
        <button
          className="whiteboard-sticky-note-delete-btn"
          onClick={(e) => {
            e.stopPropagation();
            handleDelete();
          }}
          title="Delete"
        >
          ×
        </button>
      </div>
      <div className="whiteboard-sticky-note-content">
        {isEditing ? (
          <textarea
            ref={textareaRef}
            className="whiteboard-sticky-note-textarea"
            value={content}
            onChange={handleContentChange}
            onBlur={handleContentBlur}
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '100%',
              height: '100%',
              backgroundColor: 'transparent',
              border: 'none',
              outline: 'none',
              resize: 'none',
              fontFamily: 'inherit',
              fontSize: 'inherit',
              padding: '0.5rem'
            }}
          />
        ) : (
          <div className="whiteboard-sticky-note-text">
            {content || <span className="whiteboard-sticky-note-placeholder">Double-tap or double-click to edit</span>}
          </div>
        )}
      </div>
    </div>
  );
};

export default StickyNote;

